import React, { useState, useEffect, useCallback } from 'react';
import api, { errorMessage, asArray } from '../api';
import { useAuth } from '../context/AuthContext';
import Avatar from './Avatar';
import ShareSheet from './ShareSheet';
import MeetThumbnail from './MeetThumbnail';
import { ClockIcon, MapIcon, SearchIcon, CloseIcon, BookmarkIcon, PlusIcon } from './icons';
import { formatSpan } from '../lib/datetime';

const VEHICLE_FILTERS = [
  { id: 'all', label: 'Alla' },
  { id: 'car', label: 'Bilträffar' },
  { id: 'moped', label: 'Mopedträffar' },
  { id: 'epa', label: 'EPA-träffar' },
];

const VEHICLE_LABELS = { car: 'Bilträff', moped: 'Mopedträff', epa: 'EPA-träff' };

const dayFormat = new Intl.DateTimeFormat('sv-SE', { day: 'numeric' });
const monthFormat = new Intl.DateTimeFormat('sv-SE', { month: 'short' });

const EMPTY_FORM = {
  title: '', description: '', address: '', lat: '', lng: '', event_date: '',
  end_time: '', visibility: 'public', vehicle_type: 'all',
};

/**
 * Sluttiden anges som klockslag. Ar den tidigare an starttiden menar man
 * natten efter — en traff kan halla pa fran 22 till 02.
 */
function endDateFrom(startValue, endTime) {
  if (!startValue || !endTime) return null;

  const start = new Date(startValue);
  const [hours, minutes] = endTime.split(':').map(Number);
  const end = new Date(start);
  end.setHours(hours, minutes, 0, 0);
  if (end <= start) end.setDate(end.getDate() + 1);

  return end.toISOString();
}

export default function CarMeets({
  onPickMode, pickedCoords, clearPickedCoords, newMeetsList = [], onViewProfile,
}) {
  const { user } = useAuth();
  const [meets, setMeets] = useState([]);
  const [when, setWhen] = useState('upcoming');
  const [vehicle, setVehicle] = useState('all');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sharing, setSharing] = useState(null);

  const notify = (type, text, timeout = 4000) => {
    setStatus({ type, text });
    if (timeout) setTimeout(() => setStatus(null), timeout);
  };

  const load = useCallback(async () => {
    try {
      const params = { when };
      if (vehicle !== 'all') params.vehicle = vehicle;
      if (search.trim()) params.q = search.trim();
      const res = await api.get('/meets', { params });
      setMeets(asArray(res.data));
    } catch (err) {
      notify('error', errorMessage(err, 'Träffarna kunde inte hämtas.'));
    }
  }, [when, vehicle, search]);

  // Soket vantar in skrivandet i stallet for att fraga vid varje tangent.
  useEffect(() => {
    const timer = setTimeout(load, search ? 320 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  /** Koordinater till läsbar adress, så listan kan visa var träffen är. */
  const fillAddressFrom = useCallback(async (lat, lng) => {
    const coords = { lat: lat.toFixed(6), lng: lng.toFixed(6) };
    setForm(prev => ({ ...prev, ...coords, address: 'Hämtar adress...' }));
    try {
      const res = await api.get('/geocode/reverse', { params: { lat, lng } });
      setForm(prev => ({
        ...prev, ...coords,
        address: res.data.short_name || res.data.display_name || '',
      }));
    } catch {
      // Utan adress duger koordinaterna — träffen ska gå att skapa ändå.
      setForm(prev => ({ ...prev, ...coords, address: `${coords.lat}, ${coords.lng}` }));
    }
  }, []);

  // Plats vald genom att trycka på kartan.
  useEffect(() => {
    if (!pickedCoords) return;
    setShowForm(true);
    fillAddressFrom(pickedCoords[0], pickedCoords[1]);
    clearPickedCoords();
  }, [pickedCoords, clearPickedCoords, fillAddressFrom]);

  const searchAddress = async () => {
    const address = form.address.trim();
    if (address.length < 3) return notify('error', 'Skriv minst 3 tecken.');
    try {
      const res = await api.get('/geocode/search', { params: { q: address, limit: 1 } });
      if (!res.data.length) return notify('error', 'Adressen hittades inte.');
      const hit = res.data[0];
      setForm(prev => ({
        ...prev,
        lat: hit.lat.toFixed(6),
        lng: hit.lon.toFixed(6),
        address: hit.display_name,
      }));
    } catch (err) {
      notify('error', errorMessage(err, 'Kunde inte söka adress.'));
    }
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) return notify('error', 'Enheten stödjer inte platsbestämning.');
    navigator.geolocation.getCurrentPosition(
      (pos) => fillAddressFrom(pos.coords.latitude, pos.coords.longitude),
      () => notify('error', 'Kunde inte hämta din position.')
    );
  };

  const create = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post('/meets', {
        title: form.title,
        description: form.description,
        address: form.address,
        lat: parseFloat(form.lat),
        lng: parseFloat(form.lng),
        // datetime-local ger lokal tid utan tidszon. Utan konverteringen
        // hamnar träffen fel när servern tolkar den.
        event_date: new Date(form.event_date).toISOString(),
        end_date: endDateFrom(form.event_date, form.end_time),
        visibility: form.visibility,
        vehicle_type: form.vehicle_type,
      });
      setShowForm(false);
      setForm(EMPTY_FORM);
      notify('success', 'Träffen är skapad!');
      load();
    } catch (err) {
      notify('error', errorMessage(err, 'Träffen kunde inte skapas.'));
    } finally {
      setBusy(false);
    }
  };

  /** Spara traffen. Svaret bekraftar laget sa raknaren inte glider. */
  const toggleSave = async (meet) => {
    const on = !meet.saved;
    setMeets(prev => prev.map(m => (m.id === meet.id ? { ...m, saved: on } : m)));
    try {
      if (on) await api.post(`/meets/${meet.id}/save`);
      else await api.delete(`/meets/${meet.id}/save`);
    } catch (err) {
      notify('error', errorMessage(err));
      load();
    }
  };

  const setAttendance = async (id, attending) => {
    try {
      if (attending) await api.post(`/meets/${id}/attend`);
      else await api.delete(`/meets/${id}/attend`);
      load();
    } catch (err) {
      notify('error', errorMessage(err));
    }
  };

  const deleteMeet = async (id) => {
    try {
      await api.delete(`/meets/${id}`);
      load();
    } catch (err) {
      notify('error', errorMessage(err));
    }
  };

  const addOrganiser = async (username) => {
    try {
      const res = await api.post('/friends/request', { username });
      notify('success', res.data.message);
      load();
    } catch (err) {
      notify('error', errorMessage(err));
    }
  };

  /** Knappen för att bli vän med arrangören, beroende på hur ni står. */
  const organiserAction = (meet) => {
    switch (meet.creator_relation) {
      case 'self':
        return null;
      case 'friends':
        return <span className="small faint">Ni är vänner</span>;
      case 'pending_out':
        return <span className="small faint">Vänförfrågan skickad</span>;
      default:
        return (
          <button className="btn btn--sm btn--outline" onClick={() => addOrganiser(meet.creator_name)}>
            + Lägg till vän
          </button>
        );
    }
  };

  const isPast = when === 'past';

  return (
    <div>
      {sharing && <ShareSheet meet={sharing} onClose={() => setSharing(null)} />}

      {/* Ingen egen rubrik: skarmens huvud sager redan Traffar, och
          Biltraffar krockade med filterknappen med samma namn. */}
      {!isPast && (
        <button className="create-post" onClick={() => setShowForm(!showForm)}>
          {showForm ? <CloseIcon size={18} /> : <PlusIcon size={19} />}
          {showForm ? 'Avbryt' : 'Skapa träff'}
        </button>
      )}

      <div className="meet-search">
        <span className="meet-search__icon"><SearchIcon size={17} /></span>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Sök träff, plats eller beskrivning…"
          aria-label="Sök träffar"
        />
        {search && (
          <button onClick={() => setSearch('')} aria-label="Rensa">
            <CloseIcon size={15} />
          </button>
        )}
      </div>

      <div className="map-filters">
        {VEHICLE_FILTERS.map(item => (
          <button
            key={item.id}
            className={vehicle === item.id ? 'map-filter is-active' : 'map-filter'}
            onClick={() => setVehicle(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="tabs">
        <button
          className={!isPast ? 'is-active' : ''}
          onClick={() => { setWhen('upcoming'); setExpandedId(null); }}
        >
          Kommande
        </button>
        <button
          className={isPast ? 'is-active' : ''}
          onClick={() => { setWhen('past'); setShowForm(false); setExpandedId(null); }}
        >
          Tidigare
        </button>
      </div>

      {status && <p className={`notice notice--${status.type}`}>{status.text}</p>}

      {showForm && !isPast && (
        <form onSubmit={create} className="meet-form">
          <div className="field">
            <input
              placeholder="Vad heter träffen?"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              required
              maxLength={200}
              aria-label="Titel"
            />
          </div>

          <div className="field">
            <input
              placeholder="Beskrivning (valfritt)"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              maxLength={2000}
              aria-label="Beskrivning"
            />
          </div>

          <div className="row">
            <input
              className="grow"
              placeholder="Sök adress..."
              value={form.address}
              onChange={e => setForm({ ...form, address: e.target.value })}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchAddress(); } }}
              aria-label="Adress"
            />
            <button type="button" className="btn" onClick={searchAddress}>Sök</button>
          </div>

          <div className="row" style={{ margin: 'var(--space-sm) 0' }}>
            <button type="button" className="btn grow" onClick={useCurrentLocation}>Min plats</button>
            <button type="button" className="btn grow" onClick={onPickMode}>Välj på kartan</button>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="meet-date">Från</label>
            <input
              id="meet-date"
              type="datetime-local"
              value={form.event_date}
              onChange={e => setForm({ ...form, event_date: e.target.value })}
              required
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="meet-end">
              Till (valfritt)
            </label>
            <input
              id="meet-end"
              type="time"
              value={form.end_time}
              onChange={e => setForm({ ...form, end_time: e.target.value })}
            />
            <p className="auth-form__hint" style={{ margin: '6px 0 0' }}>
              Är sluttiden tidigare än starten räknas den som natten efter.
            </p>
          </div>

          <div className="field">
            <span className="field__label">Vilken sorts träff?</span>
            <div className="map-filters">
              {VEHICLE_FILTERS.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className={form.vehicle_type === item.id ? 'map-filter is-active' : 'map-filter'}
                  onClick={() => setForm({ ...form, vehicle_type: item.id })}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <span className="field__label">Vem får se träffen?</span>
            <div className="choice">
              <button
                type="button"
                className={form.visibility === 'public' ? 'is-active' : ''}
                onClick={() => setForm({ ...form, visibility: 'public' })}
              >
                <strong>Alla på Cruze</strong>
                <span>Syns för alla, även de du inte känner</span>
              </button>
              <button
                type="button"
                className={form.visibility === 'friends' ? 'is-active' : ''}
                onClick={() => setForm({ ...form, visibility: 'friends' })}
              >
                <strong>Bara mina vänner</strong>
                <span>Syns bara för dina accepterade vänner</span>
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn--primary btn--block"
            disabled={busy || !form.lat || !form.lng}
          >
            {busy ? 'Skapar...' : 'Skapa träff'}
          </button>
        </form>
      )}

      {meets.length === 0 && (
        <p className="empty">
          {isPast ? 'Inga tidigare träffar än.' : 'Inga kommande bilträffar.'}
        </p>
      )}

      {meets.map(meet => {
        const isNew = newMeetsList.some(n => n.id === meet.id);
        const expanded = expandedId === meet.id;
        const attendees = Array.isArray(meet.attendee_names) ? meet.attendee_names : [];
        const mine = meet.creator_id === user.id;
        const start = new Date(meet.event_date);

        return (
          <article
            key={meet.id}
            className={[
              'meet-card', expanded && 'meet-card--open',
              isNew && 'meet-card--new', isPast && 'meet-card--past',
            ].filter(Boolean).join(' ')}
          >
            <MeetThumbnail lat={meet.lat} lng={meet.lng}>
              <span className="meet-card__date">
                <strong>{dayFormat.format(start)}</strong>
                <span>{monthFormat.format(start).replace('.', '').toUpperCase()}</span>
              </span>

              <span className="meet-card__flags">
                {isNew && <span className="chip chip--accent">Ny</span>}
                {VEHICLE_LABELS[meet.vehicle_type] && (
                  <span className="chip">{VEHICLE_LABELS[meet.vehicle_type]}</span>
                )}
                {meet.visibility === 'friends' && <span className="chip">Vänner</span>}
              </span>
            </MeetThumbnail>

            <div className="meet-card__body">
              <h3 className="meet-card__title">{meet.title}</h3>

              <ul className="meet-card__facts">
                <li>
                  <ClockIcon size={15} />
                  {formatSpan(meet.event_date, meet.end_date)}
                </li>
                <li>
                  <MapIcon size={15} />
                  {meet.address || `${Number(meet.lat).toFixed(4)}, ${Number(meet.lng).toFixed(4)}`}
                </li>
              </ul>

              {meet.description && (
                <p className={expanded ? 'meet-card__desc' : 'meet-card__desc meet-card__desc--clamp'}>
                  {meet.description}
                </p>
              )}

              <div className="meet-card__host">
                <Avatar src={meet.creator_picture} name={meet.creator_name} size="sm" />
                <span className="meet-card__host-text">
                  <strong>{mine ? 'Du' : meet.creator_name}</strong> arrangerar
                  {meet.attendee_count > 0 && ` · ${meet.attendee_count} kommer`}
                </span>
              </div>

              <div className="meet-card__cta">
                {isPast ? (
                  <span className="meet-card__past-note">
                    {meet.user_attending ? 'Du var med här' : 'Träffen har varit'}
                  </span>
                ) : meet.user_attending ? (
                  <button className="btn btn--outline grow" onClick={() => setAttendance(meet.id, false)}>
                    ✓ Du kommer
                  </button>
                ) : (
                  <button className="btn btn--primary grow" onClick={() => setAttendance(meet.id, true)}>
                    Jag kommer!
                  </button>
                )}

                <button
                  className="btn btn--quiet"
                  onClick={() => setExpandedId(expanded ? null : meet.id)}
                  aria-expanded={expanded}
                >
                  {expanded ? 'Mindre' : 'Läs mer'}
                </button>

                <button
                  className={meet.saved ? 'meet-card__save is-on' : 'meet-card__save'}
                  onClick={() => toggleSave(meet)}
                  aria-label={meet.saved ? 'Sparad' : 'Spara träffen'}
                  aria-pressed={Boolean(meet.saved)}
                >
                  <BookmarkIcon size={20} filled={Boolean(meet.saved)} />
                </button>
              </div>

              {expanded && (
                <div className="meet-card__more">
                  {attendees.length > 0 && (
                    <p className="meet-card__attendees">
                      <strong>Kommer:</strong> {attendees.join(', ')}
                    </p>
                  )}

                  <div className="meet-card__actions">
                    {!mine && onViewProfile && (
                      <button className="btn btn--sm btn--outline" onClick={() => onViewProfile(meet.creator_id)}>
                        Visa profil
                      </button>
                    )}
                    {!isPast && organiserAction(meet)}
                    {!isPast && (
                      <button className="btn btn--sm btn--outline" onClick={() => setSharing(meet)}>
                        Dela
                      </button>
                    )}
                    {mine && !isPast && (
                      <button className="btn btn--sm btn--quiet" onClick={() => deleteMeet(meet.id)}>
                        Ta bort
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}
